import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { validateReopenToken, getReopenPrefillData } from '../../services/pifService';
import PIFForm from '../../components/forms/PIFForm';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';

export default function PIFReopenPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');
  const [fund, setFund] = useState(null);
  const [stream, setStream] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [reopenDealId, setReopenDealId] = useState(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    async function validate() {
      try {
        const result = await validateReopenToken(token);
        if (!result.valid) {
          // Genuine token failure — this is the only path that shows "Link Expired".
          setTokenInvalid(true);
          setError('This link has expired or is no longer valid. Please contact your Mirepa analyst.');
          return;
        }
        setReopenDealId(result.dealId);

        // Everything below is served by PUBLIC Admin-SDK callables (no auth-gated
        // client reads): getPublicPIFConfig for the form structure, and
        // getReopenPrefillData for the company's prefill payload. An
        // unauthenticated company can therefore load and prefill the form.
        const getConfig = httpsCallable(functions, 'getPublicPIFConfig');
        const configResult = await getConfig({ fundId: result.fundId, streamId: result.streamId || null });
        const { fund: fundData, stream: streamData, rubric: rubricData } = configResult.data;
        setFund(fundData);
        setStream(streamData);
        setRubric(rubricData);

        const prefill = await getReopenPrefillData(token);
        setPrefillData({ company: prefill.company, responses: prefill.responses || {} });

        setValid(true);
      } catch (e) {
        // A thrown error here is a load/permission/network failure — NOT an
        // expired token (an invalid/expired token returns { valid:false } above
        // and is handled without throwing). Do not mislabel it as "Link Expired".
        setError('We couldn’t load your application right now. Please try again shortly, or contact your Mirepa analyst.');
      } finally {
        setLoading(false);
      }
    }
    validate();
  }, [token]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LoadingSpinner />
    </div>
  );

  if (!valid) return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '40px 32px', maxWidth: 480, textAlign: 'center', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>{tokenInvalid ? '⏰' : '⚠️'}</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a2744', marginBottom: 8 }}>
          {tokenInvalid ? 'Link Expired' : 'Unable to Load Application'}
        </h2>
        <p style={{ fontSize: 15, color: '#64748b' }}>{error}</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9' }}>
      <div style={{ background: '#1a2744', padding: '20px 0' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>
          <p style={{ fontSize: 12, color: '#c9952a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
            Mirepa Investment Advisors
          </p>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 }}>Update Your Application</h1>
          <p style={{ fontSize: 14, color: '#a8b4c8', marginTop: 4 }}>
            Your application has been reopened. Please review and update your responses.
          </p>
        </div>
      </div>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, marginBottom: 20, fontSize: 14, color: '#92400e' }}>
          ⚠ Your previous responses have been pre-filled. Please review and update where needed, then resubmit.
        </div>
        {fund && rubric && (
          <PIFForm fund={fund} stream={stream} rubric={rubric} prefillData={prefillData}
            dealId={reopenDealId} reopenToken={token} />
        )}
      </div>
    </div>
  );
}
